/**
 * CredentialsDialog
 * Shows branch infrastructure credentials in .env variable format.
 * Fetches from GET /api/v1/provisioning/<branch_id> (record.credentials).
 */

import { useEffect, useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Copy, Check, Loader2, KeyRound, AlertCircle } from "lucide-react";
import { getBranchInfraRecord } from "@/services/infraService";

interface CredentialsDialogProps {
  isOpen: boolean;
  onClose: () => void;
  branchId: string;
  branchName?: string;
}

function buildEnvText(creds: Record<string, unknown>): string {
  const str = (key: string) => String(creds[key] ?? "");

  const lines: string[] = [
    "# ── Identificadores ─────────────────────────────",
    `BRANCH_UUID=${str("branch_uuid")}`,
    `COMPANY_UUID=${str("company_uuid")}`,
    "",
    "# ── Backend ─────────────────────────────────────",
    `BACKEND_URL=${str("backend_domain")}`,
    "",
    "# ── Base de datos ────────────────────────────────",
    `DB_NAME=${str("database_name")}`,
    `DB_USER=${str("database_user")}`,
    `DB_PASSWORD=${str("database_password")}`,
    `DB_INTERNAL_URL=${str("internal_connection_string")}`,
    "",
    "# ── Seguridad ────────────────────────────────────",
    `SECRET_KEY=${str("backend_secret_key")}`,
    `JWT_SECRET_KEY=${str("backend_jwt_secret_key")}`,
    "",
    "# ── Dokploy ──────────────────────────────────────",
    `DOKPLOY_ENV_ID=${str("environment_id")}`,
    `DOKPLOY_ENV_NAME=${str("environment_name")}`,
    `DOKPLOY_POSTGRES_ID=${str("postgres_id")}`,
    `DOKPLOY_APP_ID=${str("application_id")}`,
  ];

  return lines.join("\n");
}

export default function CredentialsDialog({
  isOpen,
  onClose,
  branchId,
  branchName,
}: CredentialsDialogProps) {
  const [envText, setEnvText] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError]     = useState<string | null>(null);
  const [copied, setCopied]   = useState(false);

  useEffect(() => {
    if (!isOpen) return;
    setEnvText(null);
    setError(null);
    setCopied(false);
    setLoading(true);

    getBranchInfraRecord(branchId)
      .then((record) => {
        const creds = record?.credentials;
        if (!creds || Object.keys(creds).length === 0) {
          setError("Esta sucursal aún no tiene credenciales. Debe aprovisionarse primero.");
        } else {
          setEnvText(buildEnvText(creds));
        }
      })
      .catch((e) => setError(e instanceof Error ? e.message : "Error al cargar las credenciales"))
      .finally(() => setLoading(false));
  }, [isOpen, branchId]);

  const handleCopy = async () => {
    if (!envText) return;
    try {
      await navigator.clipboard.writeText(envText);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      // fallback
      const ta = document.createElement("textarea");
      ta.value = envText;
      document.body.appendChild(ta);
      ta.select();
      document.execCommand("copy");
      document.body.removeChild(ta);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="bg-slate-900 border-white/10 text-white max-w-xl w-full p-0 overflow-hidden">
        {/* Header */}
        <DialogHeader className="px-6 pt-6 pb-4 border-b border-white/10">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 rounded-xl bg-amber-500/15 border border-amber-500/30 flex items-center justify-center flex-shrink-0">
              <KeyRound className="h-4 w-4 text-amber-400" />
            </div>
            <div>
              <DialogTitle className="text-white text-base font-semibold">
                Credenciales de infraestructura
              </DialogTitle>
              {branchName && (
                <p className="text-xs text-white/40 mt-0.5">{branchName}</p>
              )}
            </div>
          </div>
        </DialogHeader>

        {/* Body */}
        <div className="px-6 py-5 space-y-4 overflow-y-auto max-h-[60vh]">
          {loading && (
            <div className="flex items-center justify-center gap-2 py-8 text-white/40">
              <Loader2 className="h-5 w-5 animate-spin" />
              <span className="text-sm">Cargando credenciales…</span>
            </div>
          )}

          {error && !loading && (
            <div className="flex items-start gap-3 rounded-xl border border-red-500/20 bg-red-500/5 px-4 py-3">
              <AlertCircle className="h-4 w-4 text-red-400 flex-shrink-0 mt-0.5" />
              <p className="text-sm text-red-400">{error}</p>
            </div>
          )}

          {envText && !loading && (
            <>
              <div className="flex items-center justify-between">
                <p className="text-xs text-white/40">
                  Variables de entorno · <span className="text-amber-400/70">mantener confidencial</span>
                </p>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={handleCopy}
                  className="h-7 px-3 text-xs gap-1.5 text-white/60 hover:text-white hover:bg-white/10"
                >
                  {copied ? (
                    <>
                      <Check className="h-3.5 w-3.5 text-emerald-400" />
                      <span className="text-emerald-400">Copiado</span>
                    </>
                  ) : (
                    <>
                      <Copy className="h-3.5 w-3.5" />
                      Copiar todo
                    </>
                  )}
                </Button>
              </div>

              <pre className="text-[11.5px] leading-relaxed font-mono text-white/80 bg-black/30 border border-white/10 rounded-xl px-4 py-4 whitespace-pre-wrap break-all">
                {envText}
              </pre>
            </>
          )}
        </div>

        {/* Footer */}
        <div className="px-6 pb-5">
          <Button
            variant="outline"
            onClick={onClose}
            className="w-full border-white/20 text-white/70 hover:bg-white/10 hover:text-white bg-transparent"
          >
            Cerrar
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
